// @spec IAM-STR-033
// @spec IAM-STR-035
// @spec SHL-STR-005
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Shield } from 'lucide-react';
import * as React from 'react';
import { toast, Toaster } from 'sonner';

import { Button } from '@/components/ui/button';
import type { AuditorAccess } from '@/features/auditor-access';
import {
  AuditorAccessList,
  GrantAuditorAccessModal,
  RevokeAuditorAccessDialog,
  useAuditorAccesses,
  useRevokeAuditorAccess,
} from '@/features/auditor-access';
import { useOrgRole } from '@/hooks/use-org-role';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { ApiClientError } from '@/types/api';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/auditors')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.auditors'),
  }),
  component: AuditorAccessPage,
});

/**
 * Auditor access management page.
 *
 * Allows organization admins to grant and revoke time-limited
 * access to external auditors.
 */
function AuditorAccessPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { organizations, isLoading: orgsLoading } = useOrganizations();

  // Get active organization
  const activeOrg = organizations.find((org) => org.isActive);

  // State
  const [page, setPage] = React.useState(1);
  const [isGrantModalOpen, setIsGrantModalOpen] = React.useState(false);
  const [accessToRevoke, setAccessToRevoke] = React.useState<AuditorAccess | null>(null);
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = React.useState(false);

  const pageSize = 20;
  const activeOrgId = activeOrg?.id;

  // Fetch auditor accesses
  const {
    data,
    isLoading: accessesLoading,
    error,
  } = useAuditorAccesses({
    orgId: activeOrgId ?? '',
    page,
    pageSize,
    enabled: Boolean(activeOrgId),
  });

  // Revoke mutation
  const revokeMutation = useRevokeAuditorAccess({
    orgId: activeOrgId ?? '',
  });

  // Handle revoke click (opens dialog)
  const handleRevokeClick = React.useCallback((access: AuditorAccess) => {
    setAccessToRevoke(access);
    setIsRevokeDialogOpen(true);
  }, []);

  // Handle revoke confirm
  const handleRevokeConfirm = React.useCallback(
    (reason?: string, notifyAuditor?: boolean) => {
      if (!accessToRevoke) return;

      const mutationParams: {
        accessId: string;
        reason?: string;
        notifyAuditor: boolean;
      } = {
        accessId: accessToRevoke.id,
        notifyAuditor: notifyAuditor ?? true,
      };
      if (reason) {
        mutationParams.reason = reason;
      }

      revokeMutation.mutate(mutationParams, {
        onSuccess: () => {
          toast.success(
            t('settings:toast.accessRevokedFor', { accessToRevoke: accessToRevoke.email })
          );
          setIsRevokeDialogOpen(false);
          setAccessToRevoke(null);
        },
        onError: (err) => {
          const message = err instanceof ApiClientError ? err.message : 'Failed to revoke access';
          toast.error(message);
        },
      });
    },
    [accessToRevoke, revokeMutation]
  );

  const { isAdmin } = useOrgRole();
  const canManageAuditorAccess = isAdmin;

  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="h-64 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">
              {t('auditorAccessPage.noOrganizationTitle')}
            </h2>
            <p className="mt-2 text-muted-foreground">
              {t('auditorAccessPage.noOrganizationDescription')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/settings/organizations' })}
              className="mt-4"
            >
              {t('auditorAccessPage.goToOrganizations')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Unauthorized - user doesn't have permission
  if (!canManageAuditorAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">
              {t('auditorAccessPage.accessDeniedTitle')}
            </h2>
            <p className="mt-2 text-muted-foreground">
              {t('auditorAccessPage.accessDeniedDescription')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/dashboard' })}
              className="mt-4"
            >
              {t('auditorAccessPage.goToDashboard')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const accesses = data?.accesses ?? [];
  const pagination = data?.pagination;
  const totalItems = pagination?.totalItems ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const showingStart = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingEnd = Math.min(page * pageSize, totalItems);

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground flex items-center gap-2">
              <Shield className="h-8 w-8" />
              {t('auditorAccessPage.title')}
            </h1>
            <p className="mt-1 text-muted-foreground">
              {t('auditorAccessPage.description', { organization: activeOrg.name })}
            </p>
          </div>
          <Button
            onClick={() => {
              setIsGrantModalOpen(true);
            }}
          >
            <Shield className="mr-2 h-4 w-4" />
            {t('auditorAccessPage.grantAccess')}
          </Button>
        </header>

        {/* Error state */}
        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : t('auditorAccessPage.failedToLoad')}
            </p>
          </div>
        )}

        {/* Auditor access list */}
        <AuditorAccessList
          accesses={accesses}
          isLoading={accessesLoading}
          onRevoke={handleRevokeClick}
        />

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Showing {showingStart}-{showingEnd} of {totalItems} accesses
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPage((p) => Math.max(1, p - 1));
                }}
                disabled={page === 1 || accessesLoading}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPage((p) => Math.min(totalPages, p + 1));
                }}
                disabled={page >= totalPages || accessesLoading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Grant Modal */}
        <GrantAuditorAccessModal
          orgId={activeOrg.id}
          isOpen={isGrantModalOpen}
          onClose={() => {
            setIsGrantModalOpen(false);
          }}
        />

        {/* Revoke Dialog */}
        <RevokeAuditorAccessDialog
          open={isRevokeDialogOpen}
          onOpenChange={setIsRevokeDialogOpen}
          access={accessToRevoke}
          onConfirm={handleRevokeConfirm}
          isLoading={revokeMutation.isPending}
        />

        {/* Toast notifications */}
        <Toaster position="top-right" richColors />
      </div>
    </div>
  );
}
